/**
 * Application Bootstrap
 * 
 * Main application class that initializes the dependency injection
 * container, registers services, and manages the application lifecycle.
 * 
 * @author Ertugrul Kücükkaya
 * @version 1.0.0
 */

import { container } from './di/container';
import { serviceRegistry, SERVICE_IDENTIFIERS } from './di/ServiceRegistry';
import { environmentService } from './config/environment';
import type { ILogger } from './services/logging/Logger';
import type { ICacheService } from './services/interfaces/IDataService';

/**
 * Application state
 */
type ApplicationState = 'uninitialized' | 'initializing' | 'running' | 'error' | 'destroyed';

/**
 * Application configuration
 */
interface ApplicationConfig {
  enableHealthChecks: boolean;
  enableGracefulShutdown: boolean;
  healthCheckInterval: number;
}

/**
 * Main Application class
 */
export class Application {
  private state: ApplicationState = 'uninitialized';
  private logger?: ILogger;
  private config: ApplicationConfig;
  private healthCheckTimer?: NodeJS.Timeout;
  private shutdownHandlers: Array<() => Promise<void>> = [];

  constructor() {
    this.config = {
      enableHealthChecks: environmentService.isDevelopment(),
      enableGracefulShutdown: true,
      healthCheckInterval: 30000, // 30 seconds
    };
  }

  /**
   * Initialize the application
   */
  public async initialize(): Promise<void> {
    if (this.state !== 'uninitialized') {
      throw new Error(`Cannot initialize application in state: ${this.state}`);
    }

    this.state = 'initializing';

    try {
      // Step 1: Register all services
      await this.registerServices();

      // Step 2: Initialize core services
      await this.initializeCoreServices();

      // Step 3: Setup health checks
      await this.setupHealthChecks();

      // Step 4: Setup graceful shutdown
      await this.setupGracefulShutdown();

      // Step 5: Validate system health
      await this.validateSystemHealth();

      this.state = 'running';
      this.logger?.info('Application initialized successfully', 'initialize', {
        environment: environmentService.get('NODE_ENV'),
        services: container.getRegisteredServices().length,
        features: this.getEnabledFeatures(),
      });

    } catch (error) {
      this.state = 'error';
      // Note: Can't use logger here as it may not be initialized yet
      console.error('Failed to initialize application:', error);
      throw error;
    }
  }

  /**
   * Get the dependency injection container
   */
  public getContainer() {
    return container;
  }

  /**
   * Get a service from the container
   */
  public getService<T>(identifier: symbol): T {
    if (this.state !== 'running') {
      throw new Error(`Cannot get service in state: ${this.state}`);
    }
    return container.resolve<T>(identifier);
  }

  /**
   * Get application state
   */
  public getState(): ApplicationState {
    return this.state;
  }

  /**
   * Check if application is ready
   */
  public isReady(): boolean {
    return this.state === 'running';
  }

  /**
   * Get application health status
   */
  public async getHealth(): Promise<{
    status: 'healthy' | 'unhealthy';
    services: Record<string, 'up' | 'down'>;
    uptime: number;
    memory: NodeJS.MemoryUsage | { heapUsed: number; heapTotal: number; external: number; rss: number };
  }> {
    const startTime = Date.now();
    const services: Record<string, 'up' | 'down'> = {};

    // Browser-safe memory usage function
    const getMemoryUsage = () => {
      if (typeof process !== 'undefined' && process.memoryUsage) {
        return process.memoryUsage();
      }
      
      // Browser fallback - use performance.memory if available
      if (typeof performance !== 'undefined' && 'memory' in performance) {
        const memory = (performance as unknown as { memory?: { totalJSHeapSize?: number; usedJSHeapSize?: number } }).memory;
        return {
          rss: 0,
          heapTotal: memory?.totalJSHeapSize || 0,
          heapUsed: memory?.usedJSHeapSize || 0,
          external: 0,
        };
      }
      
      // Final fallback
      return {
        rss: 0,
        heapTotal: 0,
        heapUsed: 0,
        external: 0,
      };
    };

    try {
      // Check core services
      if (this.logger) {
        services.logger = 'up';
      } else {
        services.logger = 'down';
      }

      // Check cache service
      try {
        const cacheService = container.resolve<ICacheService>(SERVICE_IDENTIFIERS.CACHE_SERVICE);
        const result = await cacheService.exists('health-check');
        services.cache = result.success ? 'up' : 'down';
      } catch {
        services.cache = 'down';
      }

      // Check configuration service
      try {
        const config = environmentService.getAll();
        services.config = config ? 'up' : 'down';
      } catch {
        services.config = 'down';
      }

      const allServicesUp = Object.values(services).every(status => status === 'up');

      return {
        status: allServicesUp ? 'healthy' : 'unhealthy',
        services,
        uptime: Date.now() - startTime,
        memory: getMemoryUsage(),
      };

    } catch (error) {
      this.logger?.error('Health check failed', 'getHealth', {}, error as Error);
      
      return {
        status: 'unhealthy',
        services: { error: 'down' },
        uptime: 0,
        memory: getMemoryUsage(),
      };
    }
  }

  /**
   * Gracefully shutdown the application
   */
  public async shutdown(): Promise<void> {
    if (this.state === 'destroyed') {
      return;
    }

    this.logger?.info('Shutting down application', 'shutdown');
    this.state = 'destroyed';

    try {
      // Stop health checks
      if (this.healthCheckTimer) {
        clearInterval(this.healthCheckTimer);
        this.healthCheckTimer = undefined;
      }

      // Run shutdown handlers
      await Promise.all(
        this.shutdownHandlers.map(handler => 
          handler().catch(error => {
            this.logger?.error('Shutdown handler failed', 'shutdown', {}, error);
          })
        )
      );

      // Cleanup cache service
      try {
        const cacheService = container.resolve<ICacheService>(SERVICE_IDENTIFIERS.CACHE_SERVICE);
        if ('destroy' in cacheService && typeof cacheService.destroy === 'function') {
          (cacheService as unknown as { destroy: () => void }).destroy();
        }
      } catch {
        // Ignore errors during shutdown
      }

      // Flush logger
      try {
        await this.logger?.flush();
      } catch {
        // Ignore errors during shutdown
      }

      this.logger?.info('Application shutdown completed', 'shutdown');

    } catch (error) {
      console.error('Error during application shutdown:', error);
    }
  }

  /**
   * Add a shutdown handler
   */
  public addShutdownHandler(handler: () => Promise<void>): void {
    this.shutdownHandlers.push(handler);
  }

  /**
   * Get enabled features
   */
  public getEnabledFeatures(): string[] {
    const features: string[] = [];
    
    if (environmentService.isFeatureEnabled('ENABLE_ANALYTICS')) {
      features.push('analytics');
    }
    
    if (environmentService.isFeatureEnabled('ENABLE_REAL_TIME')) {
      features.push('real-time');
    }
    
    if (environmentService.isFeatureEnabled('ENABLE_MOCK_DATA')) {
      features.push('mock-data');
    }

    return features;
  }

  /**
   * Private initialization methods
   */
  private async registerServices(): Promise<void> {
    serviceRegistry.registerServices(container);
    serviceRegistry.validateRegistration(container);
  }

  private async initializeCoreServices(): Promise<void> {
    // Initialize logger first
    this.logger = container.resolve<ILogger>(SERVICE_IDENTIFIERS.LOGGER);
    
    // Initialize cache service
    const cacheService = container.resolve<ICacheService>(SERVICE_IDENTIFIERS.CACHE_SERVICE);
    
    // Test cache functionality
    await cacheService.set('application:initialized', true, 60000);
    
    this.logger.info('Core services initialized', 'initializeCoreServices');
  }

  private async setupHealthChecks(): Promise<void> {
    if (!this.config.enableHealthChecks) {
      return;
    }

    this.healthCheckTimer = setInterval(async () => {
      try {
        const health = await this.getHealth();
        
        if (health.status === 'unhealthy') {
          this.logger?.warn('Health check failed', 'setupHealthChecks', health);
        } else {
          this.logger?.debug('Health check passed', 'setupHealthChecks', {
            uptime: health.uptime,
            memory: health.memory.heapUsed,
          });
        }
      } catch (error) {
        this.logger?.error('Health check error', 'setupHealthChecks', {}, error as Error);
      }
    }, this.config.healthCheckInterval);

    this.logger?.info('Health checks enabled', 'setupHealthChecks', {
      interval: this.config.healthCheckInterval,
    });
  }

  private async setupGracefulShutdown(): Promise<void> {
    if (!this.config.enableGracefulShutdown) {
      return;
    }

    // Only setup process handlers in Node.js environment
    if (typeof process !== 'undefined') {
      const shutdownHandler = async (signal: string) => {
        this.logger?.info('Received shutdown signal', 'setupGracefulShutdown', { signal });
        await this.shutdown();
        process.exit(0);
      };

      process.on('SIGTERM', () => shutdownHandler('SIGTERM'));
      process.on('SIGINT', () => shutdownHandler('SIGINT'));
      process.on('SIGUSR2', () => shutdownHandler('SIGUSR2')); // Nodemon

      // Handle unhandled promise rejections
      process.on('unhandledRejection', (reason, promise) => {
        this.logger?.error('Unhandled promise rejection', 'setupGracefulShutdown', {
          reason: String(reason),
          promise: String(promise),
        });
      });

      // Handle uncaught exceptions
      process.on('uncaughtException', (error) => {
        this.logger?.error('Uncaught exception', 'setupGracefulShutdown', {}, error);
        process.exit(1);
      });

      this.logger?.info('Graceful shutdown handlers registered', 'setupGracefulShutdown');
    } else {
      // Browser environment - setup beforeunload handler
      if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', () => {
          this.logger?.info('Browser window unloading, cleaning up', 'setupGracefulShutdown');
          this.shutdown().catch(console.error);
        });
        
        this.logger?.info('Browser shutdown handlers registered', 'setupGracefulShutdown');
      }
    }
  }

  private async validateSystemHealth(): Promise<void> {
    const health = await this.getHealth();
    
    if (health.status === 'unhealthy') {
      throw new Error(`System health check failed: ${JSON.stringify(health)}`);
    }

    this.logger?.info('System health validation passed', 'validateSystemHealth', health);
  }
}

// Export singleton instance
export const application = new Application();

// Export service locator for backward compatibility
export class ServiceLocator {
  public static getService<T>(identifier: symbol): T {
    return application.getService<T>(identifier);
  }

  public static isReady(): boolean {
    return application.isReady();
  }
}

// Export commonly used services for convenience
export const getLogger = () => ServiceLocator.getService<ILogger>(SERVICE_IDENTIFIERS.LOGGER);
export const getCacheService = () => ServiceLocator.getService<ICacheService>(SERVICE_IDENTIFIERS.CACHE_SERVICE);
export const getPerformanceService = () => ServiceLocator.getService<import('./services/implementations/PerformanceService').PerformanceService>(SERVICE_IDENTIFIERS.PERFORMANCE_SERVICE);

// Re-export service identifiers for convenience
export { SERVICE_IDENTIFIERS } from './di/ServiceRegistry'; 